import type { PendingChangeRequestResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetPendingChangeRequestsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetPendingChangeRequestsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { mapChangeRequestRowsToFragment } from './mapChangeRequestRowsToFragment';

type UseGetPendingChangeRequestsArgs = {
  parentId: string;
};

/**
 * Poll cadence while the parent has in-flight CRs. The Pending→terminal flip
 * is driven by OTHER actors (the merge consumer settles a recorded response
 * asynchronously), so the responder's own onSuccess invalidation races the
 * flip and the V3 stack has no subscription transport to push it — without
 * this poll the read-only/pending-deletion banners stay up until a manual
 * reload (RSP-6099 finding J). Matches `useGetChangeRequestsByParent`'s
 * 5s panel poll; entities with NO pending CRs don't poll at all.
 */
const PENDING_FLIP_POLL_INTERVAL_MS = 5000;

export const useGetPendingChangeRequests = createQueryHook<
  UseGetPendingChangeRequestsArgs,
  PendingChangeRequestResponseRow[],
  GetPendingChangeRequestsQuery
>({
  trpcQueryOptions: (trpc, { parentId }) =>
    trpc.frontend.changeRequest.pendingChangeRequests.queryOptions({
      parentId,
    }),
  trpcRefetchInterval: (rows) =>
    rows && rows.length > 0 ? PENDING_FLIP_POLL_INTERVAL_MS : false,
  mapTrpcDataToGraphQL: (trpcData) => ({
    change_request: mapChangeRequestRowsToFragment(trpcData),
  }),
  graphqlDocument: GetPendingChangeRequestsDocument,
  graphqlVariables: ({ parentId }) => ({ ParentId: parentId }),
  graphqlFetchPolicy: 'network-only',
});
